/**
 * Pure helpers for the hero at the top of "My Groups": the figures for everything the viewer looks after, taken as a
 * whole, and the groups that need their attention. The groups nest (a division holds its departments, a department its
 * groups), so the totals come from the outermost groups only, never from adding a group to the unit that already holds it.
 */

import type { GroupVendorUsageResponse, MyGroupUsageResponse, MyGroupsDailySpendResponse, MyGroupsResponse } from "../../api/organisationReporting";
import { computeGroupPace, type GroupPace, type GroupStatus } from "./groupPace";

/**
 * A group that needs a look: over its allowance, heading over it or close to it, or holding people at or near a spending
 * limit. The people are counted at the deepest group that holds them, so a department does not repeat its teams' people.
 */
export interface AttentionGroup {
  group: MyGroupUsageResponse;
  pace: GroupPace;
  /** People at a limit who are in this group but in none of the groups beneath it that the viewer can see. */
  peopleAtLimit: number;
  /** People near a limit, counted the same way. */
  peopleNearLimit: number;
  /** Red when the group is over or heading over its allowance or holds someone at a limit; amber otherwise. */
  tone: "danger" | "warning";
}

/** The leading tool across the viewer's groups, and its share of their spend as a whole percentage. */
export interface TopTool {
  /** The vendor's id, lower-cased, for its icon. */
  vendorId: string;
  name: string;
  share: number;
}

export interface GroupsOverview {
  /** The outermost groups the totals are taken from. */
  roots: MyGroupUsageResponse[];
  /** How many groups the overview covers, at every level. */
  groupCount: number;
  spend: number | null;
  /** The projected month end of the outermost groups together, or null for a past month or when any has none. */
  projected: number | null;
  /** People using AI tools across the outermost groups. Each person's spend is reported in the group that supplies their allowance. */
  people: number;
  perPerson: number | null;
  wasFullyConverted: boolean;
  /** How many of the groups have an allowance to stand against. */
  withAllowanceCount: number;
  /** The groups that need a look, the most pressing first. */
  attention: AttentionGroup[];
  /** People at a spending limit across the outermost groups. */
  peopleAtLimit: number;
  /** People near a spending limit across the outermost groups. */
  peopleNearLimit: number;
  topTool: TopTool | null;
  /** The group whose people spend the most each, when there is more than one group to compare. */
  highestPerPerson: { group: MyGroupUsageResponse; perPerson: number } | null;
}

/** Most pressing first: over the allowance already, then heading over it, then close to it. */
const severity: Partial<Record<GroupStatus, number>> = { over: 0, heading: 1, close: 2 };

/**
 * Works out the overview of the viewer's groups for one period. When the viewer owns something, the overview covers what
 * they own and everything beneath it; groups they are only a member of are about themselves, not the teams they look after.
 * Someone who owns nothing gets an overview of the groups they belong to.
 *
 * @param response - The loaded "my groups" response.
 * @param dailySpend - Each group's daily series, for its projection; null when unavailable.
 * @param isCurrentMonth - Whether the period is the current month; a past month has no projection.
 */
export function computeGroupsOverview(response: MyGroupsResponse, dailySpend: MyGroupsDailySpendResponse | null, isCurrentMonth: boolean): GroupsOverview {
  const withPeople = response.groups.filter((group) => group.personCount > 0);
  const owned = withPeople.filter((group) => group.isOwner || group.isWithinOwned);
  const scope = owned.length > 0 ? owned : withPeople;
  const roots = scope.filter((group) => !scope.some((other) => other !== group && isBeneath(group, other)));
  const period = { periodStart: response.periodStart, periodEnd: response.periodEnd, generatedAt: response.generatedAt };
  const projectionOf = (group: MyGroupUsageResponse): number | null =>
    isCurrentMonth ? (dailySpend?.groups.find((series) => series.groupKind === group.groupId)?.projection?.projectedTotal ?? null) : null;

  const spends = roots.map((group) => group.totalSpend).filter((spend): spend is number => spend !== null);
  const spend = spends.length > 0 ? spends.reduce((sum, amount) => sum + amount, 0) : null;
  const projections = roots.map(projectionOf);
  const projected = roots.length > 0 && projections.every((value) => value !== null) ? projections.reduce<number>((sum, value) => sum + (value ?? 0), 0) : null;
  const people = roots.reduce((sum, group) => sum + group.personCount, 0);

  const paces = scope.map((group) => ({
    group,
    // The allowance is held in US dollars, so it can only be set against a total in dollars.
    pace: computeGroupPace(group.totalSpend ?? 0, group.currency === "USD" ? group.totalAllowance : null, projectionOf(group), group.personCount, period, isCurrentMonth),
  }));
  const attention = paces
    .map(({ group, pace }): AttentionGroup => {
      const children = scope.filter((other) => isDirectlyBeneath(other, group));
      const peopleAtLimit = Math.max(0, group.peopleAtLimit - children.reduce((sum, child) => sum + child.peopleAtLimit, 0));
      const peopleNearLimit = Math.max(0, group.peopleNearLimit - children.reduce((sum, child) => sum + child.peopleNearLimit, 0));
      const danger = pace.status === "over" || pace.status === "heading" || peopleAtLimit > 0;
      return { group, pace, peopleAtLimit, peopleNearLimit, tone: danger ? "danger" : "warning" };
    })
    .filter((item) => severity[item.pace.status] !== undefined || item.peopleAtLimit > 0 || item.peopleNearLimit > 0)
    .sort(
      (a, b) =>
        Number(a.tone === "warning") - Number(b.tone === "warning") ||
        (severity[a.pace.status] ?? 3) - (severity[b.pace.status] ?? 3) ||
        b.pace.overPercent - a.pace.overPercent ||
        b.peopleAtLimit - a.peopleAtLimit ||
        b.peopleNearLimit - a.peopleNearLimit ||
        b.pace.spend - a.pace.spend,
    );

  const perPersonRanked = scope
    .filter((group) => group.totalSpend !== null)
    .map((group) => ({ group, perPerson: (group.totalSpend ?? 0) / group.personCount }))
    .sort((a, b) => b.perPerson - a.perPerson);

  return {
    roots,
    groupCount: scope.length,
    spend,
    projected,
    people,
    perPerson: spend !== null && people > 0 ? spend / people : null,
    wasFullyConverted: roots.every((group) => group.wasFullyConverted),
    withAllowanceCount: paces.filter(({ pace }) => pace.allowance !== null).length,
    attention,
    peopleAtLimit: roots.reduce((sum, group) => sum + group.peopleAtLimit, 0),
    peopleNearLimit: roots.reduce((sum, group) => sum + group.peopleNearLimit, 0),
    topTool: topToolOf(roots.flatMap((group) => group.vendors)),
    highestPerPerson: perPersonRanked.length > 1 ? perPersonRanked[0] : null,
  };
}

/** Whether a group sits somewhere beneath another: the other's full path starts its own breadcrumbs. */
function isBeneath(group: MyGroupUsageResponse, other: MyGroupUsageResponse): boolean {
  const otherPath = [...other.breadcrumbs, other.groupLabel];
  return group.breadcrumbs.length >= otherPath.length && otherPath.every((name, index) => group.breadcrumbs[index] === name);
}

/** Whether a group sits immediately beneath another: its breadcrumbs are exactly the other's full path. */
function isDirectlyBeneath(group: MyGroupUsageResponse, other: MyGroupUsageResponse): boolean {
  const otherPath = [...other.breadcrumbs, other.groupLabel];
  return group.breadcrumbs.length === otherPath.length && otherPath.every((name, index) => group.breadcrumbs[index] === name);
}

function topToolOf(vendors: GroupVendorUsageResponse[]): TopTool | null {
  const byVendor = new Map<string, { vendorId: string; name: string; spend: number }>();
  for (const vendor of vendors) {
    const key = vendor.vendorId.toLowerCase();
    const entry = byVendor.get(key) ?? { vendorId: key, name: vendor.vendorDisplayName, spend: 0 };
    entry.spend += vendor.totalSpend ?? 0;
    byVendor.set(key, entry);
  }
  const ranked = [...byVendor.values()].sort((a, b) => b.spend - a.spend);
  const total = ranked.reduce((sum, entry) => sum + entry.spend, 0);
  return ranked.length > 0 && total > 0 ? { vendorId: ranked[0].vendorId, name: ranked[0].name, share: Math.round((ranked[0].spend / total) * 100) } : null;
}
